import React, { useState } from "react";
import "../user_styles/userLogin.css";
import SecureNumberPad from "./SecureNumberPad";
import { useLanguage } from "../i18n/LanguageContext";
import { login, changePassword, loginErrorText } from "./customerApi";

// 1234567890 → 123-45-67890
const formatBizNo = (digits) =>
    digits.replace(/(\d{3})(\d{0,2})(\d{0,5})/, (m, a, b, c) =>
        [a, b, c].filter(Boolean).join("-")
    );

// 비밀번호 변경 권유(§14-0): 첫 비밀번호(휴대폰 뒤 4자리)를 쓰는 중이면 로그인 직후 보인다. 서버는 막지 않으므로 「나중에」로 넘어갈 수 있다.
function ChangePassword({ current, customer, onDone }) {
    const { t } = useLanguage();
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const next = pw.trim();
        if (next.length < 4 || pw.length > 32) return setError(t("pw.length"));
        if (pw !== pw2) return setError(t("pw.mismatch"));
        setBusy(true);
        try {
            await changePassword(current, pw);
            onDone({ ...customer, must_change_password: false });
        } catch (e) {
            const st = e?.response?.status, d = e?.response?.data?.detail;
            if (st === 401) setError(t("pw.wrongCurrent"));
            else if (st === 400 && d?.message) setError(d.message);
            else if (st === 422) setError(t("pw.length"));
            else setError(t("pw.fail"));
            setBusy(false);
        }
    };

    return (
        <div className="user-login-card">
            <h2 className="user-login-label" style={{ fontSize: 18, marginBottom: 6 }}>{t("pw.title")}</h2>
            <p className="user-login-hint" style={{ marginBottom: 16 }}>{t("pw.desc")}</p>
            <label className="user-login-label" htmlFor="user-pw-new">{t("pw.new")}</label>
            <input id="user-pw-new" type="password" className="user-login-field" maxLength={32}
                placeholder={t("pw.placeholder")} value={pw} onChange={(e) => setPw(e.target.value)} />
            <div className="user-login-gap"></div>
            <label className="user-login-label" htmlFor="user-pw-new2">{t("pw.confirm")}</label>
            <input id="user-pw-new2" type="password" className="user-login-field" maxLength={32}
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.key === "Enter") submit();
                }} />
            {error && <p className="user-login-error">{error}</p>}
            <button type="button" className="user-login-submit active" disabled={busy} onClick={submit}>{t("pw.submit")}</button>
            <button type="button" className="user-login-submit" disabled={busy} onClick={() => onDone(customer)}
                style={{ marginTop: 8, background: "#fff", color: "#4b5563", border: "1px solid #e5e7eb" }}>{t("pw.later")}</button>
        </div>
    );
}

export default function UserLogin({ onLogin, notice = "" }) {
    const { t } = useLanguage();
    const [bizNo, setBizNo] = useState("");
    const [password, setPassword] = useState("");
    const [showNumberPad, setShowNumberPad] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [mustChange, setMustChange] = useState(null); // 첫 비밀번호로 로그인한 매장 정보

    // 첫 비밀번호는 휴대폰 뒤 4자리, 바꾼 비밀번호는 4~32자(§14-0)
    const canSubmit = bizNo.length === 10 && password.length >= 4 && !submitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setErrorMessage("");
        try {
            const out = await login(bizNo, password);
            if (out.customer?.must_change_password) setMustChange(out.customer);
            else onLogin(out.customer);
        } catch (e) {
            setErrorMessage(loginErrorText(e, t));
        }
        setSubmitting(false);
    };

    return (
        <main className="user-login-area">
            <div className="user-login-box">
                <h1 className="user-login-title">{t("login.title")}</h1>
                <p className="user-login-subtitle">{t("login.subtitle")}</p>

                {mustChange ? (
                    <ChangePassword current={password} customer={mustChange} onDone={onLogin} />
                ) : (
                <div className="user-login-card">
                    {notice && <p className="user-login-error" role="status" style={{ marginTop: 0, marginBottom: 12 }}>{notice}</p>}
                    <label className="user-login-label">{t("login.bizLabel")}</label>
                    <button
                        type="button"
                        className={`user-login-field ${bizNo ? "filled" : ""}`}
                        onClick={() => setShowNumberPad(true)}
                    >
                        {bizNo ? formatBizNo(bizNo) : t("login.bizPlaceholder")}
                    </button>

                    <div className="user-login-gap"></div>

                    <label className="user-login-label" htmlFor="user-login-pw">{t("login.pwLabel")}</label>
                    <input
                        id="user-login-pw"
                        type="password"
                        className="user-login-field"
                        maxLength={32}
                        value={password}
                        placeholder={t("login.pwPlaceholder")}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                            if (e.key === "Enter") handleSubmit();
                        }}
                    />
                    <p className="user-login-hint">{t("login.pwHint")}</p>

                    {errorMessage && <p className="user-login-error">{errorMessage}</p>}

                    <button
                        type="button"
                        className={`user-login-submit ${canSubmit ? "active" : ""}`}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {t("login.submit")}
                    </button>
                    <p className="user-login-lock-notice">{t("login.lockNotice")}</p>
                </div>
                )}
            </div>

            <SecureNumberPad
                visible={showNumberPad}
                onConfirm={(digits) => {
                    setBizNo(digits);
                    setShowNumberPad(false);
                }}
                onCancel={() => setShowNumberPad(false)}
            />
        </main>
    );
}
